import React, { useState } from 'react';
import { X, ZoomIn } from 'lucide-react';
import type { FrameStyleOption } from '../../utils/questionnaireFrameStylesData';
import type { QuestionnaireTimberSpecies } from '../../utils/questionnaireTimberSpeciesData';

type Lightbox = { url: string; title: string } | null;

function StyleGrid({
  items,
  onEnlarge,
}: {
  items: FrameStyleOption[];
  onEnlarge: (url: string, title: string) => void;
}) {
  return (
    <div className="dq-grid">
      {items.map((style) => (
        <article key={style.id} className="dq-card structural-styles-card">
          <button
            type="button"
            className="dq-card-image structural-styles-card-image"
            onClick={() => onEnlarge(style.image, style.title)}
            aria-label={`Enlarge ${style.title}`}
          >
            <img src={style.image} alt={style.title} loading="lazy" />
            <span className="dq-image-hint structural-styles-enlarge-hint">
              <ZoomIn size={16} /> Tap to enlarge
            </span>
          </button>
          <div className="dq-card-body">
            <h3 className="dq-card-title">{style.title}</h3>
            <p className="dq-card-desc">{style.description}</p>
          </div>
        </article>
      ))}
    </div>
  );
}

export function PublicStyleGuideLightbox({
  lightbox,
  onClose,
}: {
  lightbox: Lightbox;
  onClose: () => void;
}) {
  if (!lightbox) return null;
  return (
    <div
      className="dq-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={lightbox.title}
      onClick={onClose}
    >
      <button type="button" className="dq-lightbox-close" onClick={onClose} aria-label="Close">
        <X size={24} />
      </button>
      <img
        src={lightbox.url}
        alt={lightbox.title}
        className="dq-lightbox-img"
        onClick={(e) => e.stopPropagation()}
      />
      <div className="dq-lightbox-footer" onClick={(e) => e.stopPropagation()}>
        <div className="dq-lightbox-caption">{lightbox.title}</div>
      </div>
    </div>
  );
}

export function PublicFrameStylesSection({
  frameStyles,
  onEnlarge,
}: {
  frameStyles: FrameStyleOption[];
  onEnlarge: (url: string, title: string) => void;
}) {
  return (
    <section className="dq-section public-style-section--plain">
      <StyleGrid items={frameStyles} onEnlarge={onEnlarge} />
    </section>
  );
}

export function PublicTimberSpeciesSection({
  timberSpecies,
}: {
  timberSpecies: QuestionnaireTimberSpecies[];
}) {
  const [speciesIdx, setSpeciesIdx] = useState(0);
  const sp = timberSpecies[speciesIdx];
  if (!sp) return null;

  return (
    <section className="dq-section public-style-section--plain">
      <h2 className="dq-section-title">Wood species</h2>
      <div className="dq-sp-tabs">
        {timberSpecies.map((s, i) => (
          <button
            key={s.id}
            type="button"
            className={`dq-sp-tab ${i === speciesIdx ? 'dq-sp-tab--active' : ''}`}
            onClick={() => setSpeciesIdx(i)}
          >
            {s.name.split(' ').pop()}
          </button>
        ))}
      </div>
      <div className="dq-sp-card" key={sp.id}>
        {sp.imageUrl && (
          <div className="dq-sp-image">
            <img src={sp.imageUrl} alt={`${sp.name} timber frame`} />
          </div>
        )}
        <div className="dq-sp-content">
          <div className="dq-sp-header">
            <div>
              <div className="dq-sp-name-row">
                <span className="dq-sp-name">{sp.name}</span>
                <span className="dq-sp-cost">{sp.cost}</span>
              </div>
              <span className="dq-sp-latin">{sp.latin}</span>
            </div>
            <div className="dq-sp-attr-row">
              <div className="dq-sp-attr">
                <span className="dq-sp-attr-l">Hardness</span>
                <span className="dq-sp-attr-v">{sp.hardness}</span>
              </div>
              <div className="dq-sp-attr">
                <span className="dq-sp-attr-l">Grain</span>
                <span className="dq-sp-attr-v">{sp.grain}</span>
              </div>
              <div className="dq-sp-attr">
                <span className="dq-sp-attr-l">Durability</span>
                <span className="dq-sp-attr-v">{sp.durability}</span>
              </div>
            </div>
          </div>
          <p className="dq-sp-desc">{sp.description}</p>
          <div className="dq-sp-tags">
            {sp.pros.map((pro, pi) => (
              <span key={`p${pi}`} className="dq-sp-tag dq-sp-tag--pro">
                ✓ {pro}
              </span>
            ))}
            {sp.cons.map((con, ci) => (
              <span key={`c${ci}`} className="dq-sp-tag dq-sp-tag--con">
                △ {con}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
